import { useState } from "react"
import { Search, X } from "lucide-react"
import { SectionHeader } from "@/components/ui/headers"
import { CategoryChips } from "./category-chips"
import { filterMenuItems, groupMenuItemsByCategory } from "./menu-filtering"
import { DishDetailDrawer } from "./dish-detail-drawer"
import { ClosedNotice } from "./closed-notice"
import { DishListItem } from "./dish-list-item"
import { usePickupHours } from "./use-pickup-hours"
import { FeaturedCard } from "./featured-card"
import type { DinerSummary, MenuItem, MenuPayload, OrderServiceType, ServiceContext } from "./types"
import { t } from "@/lib/i18n"

type MenuScreenProps = {
  menu: MenuPayload
  /**
   * The visit behind the menu, for dine-in. Null on a pickup menu, which is
   * addressed by slug and has no visit at all — so `serviceContext` is passed
   * rather than derived from here.
   */
  diner?: DinerSummary
  serviceContext?: ServiceContext
  /**
   * The pre-seating menu: browsing only, before anyone has opened the table.
   * Suppresses every add-to-cart control rather than forking the menu's
   * shaping logic — a `readOnly` prop, not a third `ServiceContext` value,
   * because this screen has no order and `ServiceContext` answers "which rail
   * is this order on".
   */
  readOnly?: boolean
}

export function MenuScreen({ diner = null, menu, serviceContext: contextProp, readOnly = false }: MenuScreenProps) {
  const serviceContext = contextProp ?? diner?.serviceContext ?? "dine_in"
  const isPickup = serviceContext === "pickup"
  const [activeCategory, setActiveCategory] = useState<number | "all">("all")
  const [query, setQuery] = useState("")
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const { closed: pickupClosed, opensAt, timeZone } = usePickupHours()

  const openDish = (item: MenuItem) => {
    setSelectedItem(item)
    setDetailOpen(true)
  }

  const searching = query.trim().length > 0
  const visibleItems = filterMenuItems(menu.items, { query, categoryId: activeCategory })
  // The "all" tab reads as a real menu only when it keeps its category sections;
  // a single category or an active search is already narrow enough to stay flat.
  const grouped = !searching && activeCategory === "all"
  const groups = grouped
    ? groupMenuItemsByCategory(visibleItems, menu.categories, t("ui.user.home.uncategorized"))
    : []

  const featured = menu.featuredItems.length > 0 ? menu.featuredItems : menu.items.slice(0, 6)
  const sectionLabel = searching
    ? t("ui.react.features.user.home_screen.results")
    : activeCategory === "all"
      ? t("ui.user.home.all_menu")
      : menu.categories.find((category) => category.id === activeCategory)?.name ??
        t("ui.user.home.default_menu")

  return (
    <div className="text-foreground" data-testid="menu-screen-view">
      {/* Above the sticky search rather than inside it: the menu stays fully
          readable and scrollable when the kitchen is shut, so this states the
          fact once at the top instead of following the diner down the list. What
          it changes is the add controls, which drop out on their own. */}
      {pickupClosed && (
        <ClosedNotice opensAt={opensAt} timeZone={timeZone} className="mx-4 mt-3" />
      )}

      {/* Sticky search + category nav. Location, table, bell and avatar belong
          to the shell top bar, so the menu opens straight into search. */}
      <div className="sticky top-0 z-10 space-y-3 border-b bg-background/95 px-4 pb-3 pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/75">
        <div className="relative md:max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("ui.react.features.user.home_screen.search_dish_ingredient")}
            aria-label={t("ui.react.features.user.home_screen.search_dish_ingredient")}
            data-testid="menu-search"
            className="h-11 w-full rounded-full border bg-card pl-10 pr-10 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring md:h-10 [&::-webkit-search-cancel-button]:hidden"
          />
          {searching && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("ui.react.features.user.home_screen.clear_search")}
              data-testid="menu-search-clear"
              className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="-mx-4">
          <CategoryChips categories={menu.categories} activeId={activeCategory} onChange={setActiveCategory} />
        </div>
      </div>

      <div className="pb-8">
        {!searching && featured.length > 0 && (
          <>
            <SectionHeader
              title={t("ui.react.features.user.home_screen.featured_house")}
              className="mt-5"
            />
            <div className="flex gap-3 overflow-x-auto px-4 pb-1.5 scrollbar-none [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-2 md:gap-4 md:overflow-visible lg:grid-cols-3">
              {featured.map((item) => (
                <FeaturedCard key={item.id} item={item} onSelect={openDish} />
              ))}
            </div>
          </>
        )}

        <SectionHeader
          title={sectionLabel}
          trailing={
            <span className="text-xs font-normal text-muted-foreground">
              {t("ui.user.common.products_count", { count: visibleItems.length })}
            </span>
          }
          className="mt-6"
        />

        <div className="px-4" data-testid="dish-list">
          {visibleItems.length === 0 ? (
            <div
              className="py-10 text-center text-sm text-muted-foreground"
              data-testid="dish-list-empty"
            >
              {searching
                ? t("ui.react.features.user.home_screen.no_results", { query: query.trim() })
                : t("ui.react.features.user.home_screen.no_products_in_category")}
            </div>
          ) : grouped ? (
            groups.map((group) => (
              <section
                key={String(group.id)}
                data-testid={`dish-group-${group.id}`}
                aria-labelledby={`dish-group-heading-${group.id}`}
                className="pt-5 first:pt-0"
              >
                <h4
                  id={`dish-group-heading-${group.id}`}
                  className="flex items-baseline justify-between gap-2 border-b border-border/60 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  <span className="flex min-w-0 items-center gap-1.5 truncate">
                    {group.emoji && (
                      <span className="text-sm leading-none" aria-hidden="true">
                        {group.emoji}
                      </span>
                    )}
                    {group.name}
                  </span>
                  <span className="shrink-0 font-normal normal-case tracking-normal">
                    {t("ui.user.common.products_count", { count: group.items.length })}
                  </span>
                </h4>
                <DishGrid
                  items={group.items}
                  onSelect={openDish}
                  serviceType={isPickup ? "pick_up" : "eat_in"}
                  readOnly={readOnly}
                />
              </section>
            ))
          ) : (
            <DishGrid
              items={visibleItems}
              onSelect={openDish}
              serviceType={isPickup ? "pick_up" : "eat_in"}
              readOnly={readOnly}
            />
          )}
        </div>
      </div>

      <DishDetailDrawer
        item={selectedItem}
        billId={diner?.billId ?? null}
        serviceContext={serviceContext}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        readOnly={readOnly}
      />
    </div>
  )
}

type DishGridProps = {
  items: MenuItem[]
  onSelect: (item: MenuItem) => void
  serviceType: OrderServiceType
  readOnly?: boolean
}

/** One column on mobile, multi-column from md up — shared by the flat and grouped lists. */
function DishGrid({ items, onSelect, serviceType, readOnly }: DishGridProps) {
  return (
    <div className="md:grid md:grid-cols-2 md:gap-x-8 lg:grid-cols-3">
      {items.map((item) => (
        <DishListItem
          key={item.id}
          item={item}
          onSelect={onSelect}
          serviceType={serviceType}
          readOnly={readOnly}
        />
      ))}
    </div>
  )
}

